// Inline editor for a single proposed field. Renders the control for the
// field's configured edit kind (text / textarea / select / multiselect) or
// read-only text; the textarea auto-grows to fit multi-paragraph content.
import React, { useRef } from 'react';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import { useAutosizeTextArea } from 'components/DesignSystems/New/PratusTextArea';
import {
  humanizeFieldName,
  isPlainObject,
  type FieldConfig,
  type SelectOption,
} from '../../confirmationFieldConfig';

export const formatValue = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map((v) => formatValue(v)).join(', ');
  // Plain objects read as labeled pairs, never raw JSON.
  if (isPlainObject(value)) {
    return Object.entries(value)
      .map(([k, v]) => `${humanizeFieldName(k)}: ${formatValue(v)}`)
      .join(' · ');
  }
  return JSON.stringify(value);
};

export const asEditableText = (value: unknown): string =>
  typeof value === 'string' ? value : value == null ? '' : formatValue(value);

const ConfirmationField: React.FC<{
  config: FieldConfig;
  options: SelectOption[];
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ config, options, value, onChange }) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const textAreaValue = asEditableText(value);
  useAutosizeTextArea(textAreaRef, textAreaValue, 3);

  // 'object' is edited via ObjectDiffRows in ChangeReview; render read-only
  // here so an object value never lands in a plain text input.
  if (config.editKind === 'readonly' || config.editKind === 'object') {
    return <span className="text-break">{formatValue(value)}</span>;
  }
  if (config.editKind === 'select' || config.editKind === 'multiselect') {
    const isMulti = config.editKind === 'multiselect';
    const find = (v: string): SelectOption =>
      options.find((o) => o.value === v) ?? { value: v, label: v };
    const selectValue = isMulti
      ? (Array.isArray(value) ? value : []).map((v) => find(String(v)))
      : value == null || value === ''
        ? null
        : find(String(value));
    return (
      <StylishNewSelect
        options={options}
        value={selectValue}
        onChange={(selection: SelectOption | SelectOption[] | null) =>
          onChange(
            isMulti
              ? (Array.isArray(selection) ? selection : []).map((o) => o.value)
              : selection && !Array.isArray(selection)
                ? selection.value
                : null
          )
        }
        isMulti={isMulti}
        isClearable
        isSearchable
        placeholder="Select…"
      />
    );
  }
  if (config.editKind === 'textarea') {
    return (
      <textarea
        ref={textAreaRef}
        className="form-control tw:max-h-[360px] tw:w-full tw:resize-y tw:overflow-y-auto"
        rows={3}
        value={textAreaValue}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  // text
  return (
    <input
      type="text"
      className="form-control"
      value={textAreaValue}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

export default ConfirmationField;
